import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Filter, Inbox, Search } from 'lucide-react';
import {
  useInvoices,
  useInvoice,
  EMPTY_FILTERS,
  SORT_KEYS,
  type InvoiceListFilters,
  type SortDir,
  type SortKey,
  type State,
} from '../../api/invoices.js';
import { useDashboardKpis } from '../../api/kpis.js';
import { FilterPills } from './FilterPills.js';
import { InvoiceFilterPills, type ViewKey } from './InvoiceFilterPills.js';
import { useInvoiceListKeyboard } from './useInvoiceListKeyboard.js';
import { InvoiceRow } from './InvoiceRow.js';
import { EmptyState } from '../../components/EmptyState.js';
import { InvoiceListShortcutsFooter } from './InvoiceListShortcutsFooter.js';
import { InvoiceDetail } from './InvoiceDetail.js';
import { InvoiceDetailRail } from './InvoiceDetailRail.js';
import { useIsWide } from '../../lib/breakpoints.js';
import { PageWrapper } from '../../shell/PageWrapper.js';
import type { Membership } from '../../api/types.js';

const SKELETON_ROWS = 6;
const SIREN_DEBOUNCE_MS = 300;
const SEARCH_DEBOUNCE_MS = 300;

interface Props {
  tenantId: string;
  tenantName: string;
  // Caller's role on this tenant — forwarded to InvoiceDetail when the
  // split-pane mode is active so action buttons can be role-gated.
  role?: Membership['role'] | null;
}

function filtersFromSearchParams(sp: URLSearchParams): InvoiceListFilters {
  const direction = sp.get('direction');
  const stateParam = sp.get('state');
  const states = stateParam ? (stateParam.split(',').filter(Boolean) as State[]) : [];
  const sortByParam = sp.get('sortBy');
  const sortBy: SortKey | null =
    sortByParam && (SORT_KEYS as readonly string[]).includes(sortByParam)
      ? (sortByParam as SortKey)
      : null;
  const sortDir: SortDir = sp.get('sortDir') === 'desc' ? 'desc' : 'asc';
  return {
    direction:
      direction === 'INBOUND' || direction === 'OUTBOUND' || direction === 'OFF_PLATFORM'
        ? direction
        : null,
    states,
    q: sp.get('q') ?? '',
    dateFrom: sp.get('dateFrom') || null,
    dateTo: sp.get('dateTo') || null,
    senderSiren: sp.get('senderSiren') ?? '',
    sortBy,
    sortDir,
  };
}

function filtersToSearchParams(f: InvoiceListFilters): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.direction) sp.set('direction', f.direction);
  if (f.states.length > 0) sp.set('state', f.states.join(','));
  if (f.q) sp.set('q', f.q);
  if (f.dateFrom) sp.set('dateFrom', f.dateFrom);
  if (f.dateTo) sp.set('dateTo', f.dateTo);
  if (f.senderSiren) sp.set('senderSiren', f.senderSiren);
  if (f.sortBy) {
    sp.set('sortBy', f.sortBy);
    sp.set('sortDir', f.sortDir);
  }
  return sp;
}

const SORT_LABEL_FR: Record<SortKey, string> = {
  'sender.name': 'Fournisseur',
  invoiceNumber: 'N° facture',
  amountTotal: 'Montant',
  dueDate: 'Échéance',
  state: 'État',
};

function normaliseDates(f: InvoiceListFilters): InvoiceListFilters {
  if (f.dateFrom && f.dateTo && f.dateFrom > f.dateTo) {
    return { ...f, dateFrom: f.dateTo, dateTo: f.dateFrom };
  }
  return f;
}

export function InvoiceListScreen({ tenantId, tenantName, role = null }: Props) {
  const [sp, setSp] = useSearchParams();
  const { primaryKey: selectedPrimaryKey } = useParams<{ primaryKey: string }>();
  const filters = filtersFromSearchParams(sp);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  const queryClient = useQueryClient();
  // Apply silent date-swap before passing to data layer.
  const query = useInvoices(tenantId, normaliseDates(filters), cursor);
  const isWide = useIsWide();
  const navigate = useNavigate();
  const location = useLocation();
  // REQ-FE-031: at ≥1280px the split-pane is the *default* visual
  // experience. If no row is selected, auto-select the first row in
  // the page so centre + right panes always render. Below 1280px the
  // single-pane mode keeps the previous behaviour (click row →
  // navigate to detail route).
  const splitPane = isWide;
  // Lookup the selected invoice for the right-rail's history + PDF
  // placeholder. Cache is shared with the centre-pane InvoiceDetail via
  // the same react-query key (`['invoice', primaryKey]`).
  const selectedInvoiceQ = useInvoice(splitPane ? (selectedPrimaryKey ?? '') : '');
  // REQ-FE-031: pull tenant KPIs to surface live counts on the filter
  // pills (Toutes / À traiter / Urgentes / Litiges / Approuvées). When
  // the endpoint errors the counts simply hide — see
  // REQ-BE-031-kpi-triage for the live-env failure mode.
  const kpis = useDashboardKpis(tenantId);

  // SIREN debounce: a local input value mirrors filters.senderSiren and
  // commits to the URL after SIREN_DEBOUNCE_MS of inactivity.
  const [sirenInput, setSirenInput] = useState(filters.senderSiren);
  useEffect(() => {
    if (sirenInput === filters.senderSiren) return;
    const handle = window.setTimeout(() => {
      applyFilters({ ...filters, senderSiren: sirenInput });
    }, SIREN_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [sirenInput]);

  // REQ-FE-031: free-text search input (Rechercher fournisseur, référence…).
  // Same debounce pattern as SIREN, drives `filters.q`.
  const [searchInput, setSearchInput] = useState(filters.q);
  useEffect(() => {
    if (searchInput === filters.q) return;
    const handle = window.setTimeout(() => {
      applyFilters({ ...filters, q: searchInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // REQ-FE-031 task A6: when split-pane is active and no row is
  // selected via URL, auto-select the first row in the page so the
  // centre+right panes always render. URL update uses `replace: true`
  // so the back button still leaves the list.
  const items = query.data?.items ?? [];
  useEffect(() => {
    if (!splitPane) return;
    if (selectedPrimaryKey) return;
    if (items.length === 0) return;
    const firstPk = items[0]!.primaryKey;
    navigate(`/t/${tenantId}/invoices/${encodeURIComponent(firstPk)}${location.search}`, {
      replace: true,
    });
  }, [splitPane, selectedPrimaryKey, items, tenantId, navigate, location.search]);

  function applyFilters(next: InvoiceListFilters) {
    setSp(filtersToSearchParams(next), { replace: true });
    setCursorStack([null]);
  }

  function clearAll() {
    setSirenInput('');
    setSearchInput('');
    applyFilters(EMPTY_FILTERS);
  }

  function onDateFrom(e: ChangeEvent<HTMLInputElement>) {
    applyFilters({ ...filters, dateFrom: e.target.value || null });
  }
  function onDateTo(e: ChangeEvent<HTMLInputElement>) {
    applyFilters({ ...filters, dateTo: e.target.value || null });
  }

  function goNextPage() {
    if (query.data?.nextCursor) {
      setCursorStack((s) => [...s, query.data!.nextCursor]);
    }
  }
  function goPrevPage() {
    setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }
  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['invoices', tenantId] });
  }

  const showingSkeleton = query.isLoading && !query.data;
  const pageIndex = cursorStack.length - 1;
  const hasActiveFilter =
    filters.direction !== null ||
    filters.states.length > 0 ||
    filters.q.length > 0 ||
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.senderSiren.length > 0;

  const selectedInvoice = selectedInvoiceQ.data ?? null;

  // REQ-FE-029a-2: j/k navigation + d/v/a action dispatch only when
  // split-pane is active.
  useInvoiceListKeyboard({
    enabled: splitPane,
    items,
    selectedPrimaryKey,
    tenantId,
  });

  // REQ-FE-031 task A2: build the counts map for InvoiceFilterPills.
  // À traiter / Urgentes / Litiges / Approuvées come from the tenant
  // KPIs. Toutes falls back to a client-side derived sum because the
  // contract has no `total-invoices` KPI field. When KPIs error, all
  // counts are undefined → the badges hide gracefully.
  const viewCounts = useMemo<Partial<Record<ViewKey, number>>>(() => {
    if (!kpis.data) return {};
    const todo = kpis.data.openAp?.count ?? null;
    const urgent = kpis.data.overdue?.count ?? null;
    const disputed = kpis.data.disputed?.count ?? null;
    const approved = kpis.data.approvedLast30d?.count ?? null;
    // Toutes ≈ open AP + approved + disputed (mockup shows 8 for a
    // 4+1+3 split). This is an approximation until BE exposes a
    // total-invoices field.
    const all = (todo ?? 0) + (disputed ?? 0) + (approved ?? 0);
    const out: Partial<Record<ViewKey, number>> = {};
    if (todo !== null) out.todo = todo;
    if (urgent !== null) out.urgent = urgent;
    if (disputed !== null) out.disputed = disputed;
    if (approved !== null) out.approved = approved;
    if (todo !== null || disputed !== null || approved !== null) out.all = all;
    return out;
  }, [kpis.data]);

  const listPane = (
    <section
      className={`invoices${splitPane ? ' invoices--pane' : ''}`}
      aria-label="Liste des factures"
    >
      {/* REQ-FE-031 task A10: page header is the mockup title
          `Factures reçues.` (with period) — no subtitle. Refresh
          control still available via the Filtres dropdown so power
          users can force-refetch without leaving the page. */}
      <header className="invoices__head" data-testid="invoices-head">
        <div className="invoices__head-title-block">
          {/* REQ-FE-037 item 6: eyebrow drops `SOCIÉTÉ ·` prefix —
              mockup `03-invoice-list.png` shows two parts only. */}
          <p
            className="text-[9px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: 'var(--gray-500)' }}
            data-testid="invoices-eyebrow"
          >
            {tenantName.toUpperCase()} · BOÎTE DE RÉCEPTION
          </p>
          <h1 className="display invoices__title" data-testid="invoices-title">
            Factures reçues<span className="invoices__title-accent">.</span>
          </h1>
        </div>
      </header>

      {/* REQ-FE-037 item 5: list toolbar is a single flex row with the
          filter pills on the left and search + Filtres on the right.
          Previously the pills lived on a row of their own below the
          search/Filtres pair which sat next to the h1. */}
      <div className="invoices__toolbar" data-testid="invoices-toolbar">
        <InvoiceFilterPills filters={filters} onChange={applyFilters} counts={viewCounts} />
        <div className="invoices__toolbar-right">
          <label
            className="invoices__search"
            aria-label="Rechercher dans les factures"
            data-testid="invoices-search-label"
          >
            <Search
              className="invoices__search-icon"
              aria-hidden="true"
              size={14}
              strokeWidth={1.5}
            />
            <input
              type="search"
              className="invoices__search-input"
              placeholder="Rechercher fournisseur, référence…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              data-testid="invoices-search"
            />
          </label>
          <details className="invoices__filtres" data-testid="invoices-filtres">
            <summary className="invoices__filtres-toggle" data-testid="invoices-filtres-toggle">
              <Filter aria-hidden="true" size={14} strokeWidth={1.5} />
              <span>Filtres</span>
            </summary>
            <div className="invoices__filtres-panel" data-testid="invoices-filtres-panel">
              <FilterPills filters={filters} onChange={applyFilters} />
              <div className="invoices__advanced-filters" data-testid="invoices-advanced-filters">
                <label className="invoices__advanced-field">
                  <span className="eyebrow">Du</span>
                  <input
                    type="date"
                    value={filters.dateFrom ?? ''}
                    onChange={onDateFrom}
                    data-testid="filter-date-from"
                  />
                </label>
                <label className="invoices__advanced-field">
                  <span className="eyebrow">Au</span>
                  <input
                    type="date"
                    value={filters.dateTo ?? ''}
                    onChange={onDateTo}
                    data-testid="filter-date-to"
                  />
                </label>
                <label className="invoices__advanced-field">
                  <span className="eyebrow">SIREN du fournisseur</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{0,9}"
                    maxLength={9}
                    placeholder="ex. 552100891"
                    value={sirenInput}
                    onChange={(e) => setSirenInput(e.target.value.replace(/\D/g, ''))}
                    data-testid="filter-siren"
                  />
                </label>
                <label className="invoices__advanced-field">
                  <span className="eyebrow">Trier par</span>
                  <select
                    value={filters.sortBy ?? ''}
                    onChange={(e) => {
                      const v = e.target.value as SortKey | '';
                      applyFilters({
                        ...filters,
                        sortBy: v === '' ? null : v,
                        sortDir: 'asc',
                      });
                    }}
                    data-testid="sort-select"
                  >
                    <option value="">Par défaut</option>
                    {SORT_KEYS.map((k) => (
                      <option key={k} value={k}>
                        {SORT_LABEL_FR[k]}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={filters.sortBy === null}
                  onClick={() =>
                    applyFilters({
                      ...filters,
                      sortDir: filters.sortDir === 'asc' ? 'desc' : 'asc',
                    })
                  }
                  aria-label={filters.sortDir === 'asc' ? 'Tri ascendant' : 'Tri descendant'}
                  data-testid="sort-dir-toggle"
                >
                  {filters.sortDir === 'asc' ? '▲' : '▼'}
                </button>
                {hasActiveFilter ? (
                  <button
                    type="button"
                    className="btn-ghost invoices__clear-filters"
                    onClick={clearAll}
                    data-testid="filter-clear"
                  >
                    Effacer les filtres
                  </button>
                ) : null}
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={refresh}
                  disabled={query.isFetching}
                  data-testid="invoices-refresh"
                >
                  {query.isFetching ? 'Actualisation…' : 'Actualiser'}
                </button>
              </div>
              {/* REQ-FE-031: legacy sort-header buttons are kept inside
                  the Filtres dropdown so existing keyboard / screen-reader
                  flows (and the sort-header tests) keep working. They
                  no longer appear in the primary toolbar per the mockup. */}
              <div className="invoices__legacy-sort" data-testid="invoices-legacy-sort">
                {(SORT_KEYS as readonly SortKey[]).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`pill-btn${filters.sortBy === k ? ' is-active' : ''}`}
                    onClick={() => {
                      if (filters.sortBy !== k) {
                        applyFilters({ ...filters, sortBy: k, sortDir: 'asc' });
                      } else if (filters.sortDir === 'asc') {
                        applyFilters({ ...filters, sortBy: k, sortDir: 'desc' });
                      } else {
                        applyFilters({ ...filters, sortBy: null, sortDir: 'asc' });
                      }
                    }}
                    data-testid={`sort-header-${k}`}
                  >
                    {SORT_LABEL_FR[k]}
                    {filters.sortBy === k ? (filters.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </button>
                ))}
              </div>
            </div>
          </details>
        </div>
      </div>

      {query.isError ? (
        <div className="invoices__error" role="alert">
          <span>Impossible de charger les factures.</span>
          <button type="button" className="btn-ghost" onClick={() => query.refetch()}>
            Réessayer
          </button>
        </div>
      ) : null}

      <div className="invoices__list" data-testid="invoices-table">
        {showingSkeleton ? (
          Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <div
              key={i}
              className="invoice-row invoice-row--skeleton"
              data-testid="invoice-skeleton"
            >
              <span />
              <span />
              <span />
            </div>
          ))
        ) : items.length === 0 ? (
          hasActiveFilter ? (
            <EmptyState
              testId="invoices-empty"
              variant="card"
              icon={<Search aria-hidden="true" size={20} strokeWidth={1.5} />}
              title="Aucune facture"
              description="Aucune facture ne correspond à vos filtres actuels."
              action={{
                label: 'Effacer les filtres',
                onClick: clearAll,
                testId: 'invoices-empty-clear',
              }}
            />
          ) : (
            <EmptyState
              testId="invoices-empty"
              variant="card"
              icon={<Inbox aria-hidden="true" size={20} strokeWidth={1.5} />}
              title="Pas encore de factures"
              description="Les factures reçues d'Iopole apparaîtront ici dès leur traitement."
              action={{
                label: 'Importer une facture',
                href: `/t/${tenantId}/uploads`,
                testId: 'invoices-empty-upload',
              }}
            />
          )
        ) : (
          items.map((invoice) => (
            <InvoiceRow
              key={invoice.primaryKey}
              invoice={invoice}
              tenantId={tenantId}
              selected={selectedPrimaryKey === invoice.primaryKey}
            />
          ))
        )}
      </div>

      {(items.length > 0 || pageIndex > 0) && !showingSkeleton ? (
        <nav className="invoices__pager" aria-label="Pagination">
          <button
            type="button"
            className="btn-ghost"
            onClick={goPrevPage}
            disabled={pageIndex === 0 || query.isFetching}
            data-testid="invoices-prev"
          >
            ← Précédent
          </button>
          <span className="invoices__pager-info">Page {pageIndex + 1}</span>
          <button
            type="button"
            className="btn-ghost"
            onClick={goNextPage}
            disabled={!query.data?.nextCursor || query.isFetching}
            data-testid="invoices-next"
          >
            Suivant →
          </button>
        </nav>
      ) : null}

      {splitPane ? <InvoiceListShortcutsFooter /> : null}
    </section>
  );

  if (!splitPane) {
    return <PageWrapper>{listPane}</PageWrapper>;
  }

  return (
    <PageWrapper size="wide">
      <div
        className="invoice-list-split"
        data-testid="invoice-list-split"
        data-selected-pk={selectedPrimaryKey ?? ''}
      >
        {listPane}
        <div className="invoice-list-split__centre" data-testid="invoice-list-split-centre">
          {selectedPrimaryKey ? (
            <InvoiceDetail
              tenantId={tenantId}
              tenantName={tenantName}
              role={role}
              paneMode="centre"
              primaryKey={selectedPrimaryKey}
            />
          ) : (
            <EmptyState
              testId="invoice-list-split-empty"
              variant="card"
              icon={<Inbox aria-hidden="true" size={20} strokeWidth={1.5} />}
              title="Sélectionnez une facture"
              description="Choisissez une facture dans la liste pour voir son détail ici."
            />
          )}
        </div>
        {selectedInvoice ? <InvoiceDetailRail invoice={selectedInvoice} /> : null}
      </div>
    </PageWrapper>
  );
}
